// src/components/Dashboard.js
import React, { useEffect, useState } from 'react';
import { Container, Button, Card, ListGroup, Spinner, Alert } from 'react-bootstrap';
import { LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useToken from '../../hooks/useToken';
import './Dashboard.css';
import ProgressTracker from '../shared/ProgressTracker';
import useProgressRedirect from '../../hooks/useProgressRedirect';

function Dashboard() {
  // ✅ Use `isReady` from useToken to check if synchronization is done
  const { token, isReady, refreshAccessToken, removeToken } = useToken();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useProgressRedirect("NOT_STARTED"); // <=== Auto redirect if necessary!

  // ✅ Ensure we wait until the token is ready
  useEffect(() => {
    const checkLogin = async () => {
      let authToken = token;
      if (!authToken) {
        console.warn("Token not found, attempting refresh...");
        authToken = await refreshAccessToken();
        if (!authToken) {
          console.error("Token refresh failed, redirecting to login.");
          removeToken();
          navigate("/", { replace: true });
          return;
        }
      }

      if (localStorage.getItem('role') !== 'PLAYER') {
        navigate('/', { replace: true });
        return;
      }
      console.log("Dashboard loaded with token:", authToken);
    };
    checkLogin();
  }, [token, navigate, refreshAccessToken, removeToken]);

  // ✅ Handle Pre-Experiment Start
  const handleConsent = async () => {
    setLoading(true);
    try {
      const authToken = token || await refreshAccessToken();

      const res = await fetch("/api"+'/player/progress', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ progress_step: "CONSENT" }),
      });

      console.log('Progress update response:', res);

      if (!res.ok) {
        throw new Error("Progress update failed");
      }
      navigate('/consent', { replace: true });
      return;
    } catch (err) {
      console.error('Failed to update progress:', err);
    }
    setLoading(false);
  };

  // ✅ Now safe to render the page
  return (
    <Container className="dashboard-container py-5">
      <ProgressTracker currentStep="not_started" />
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Card className="shadow-lg border-0">
        <Card.Body>
          <h2 className="mb-3">
            🔐 The Role of Design Documentation in <br />
            Supporting Web Application Penetration Testing
          </h2>
          <p className="text-muted lead">
            The study explores how access to specific documentation affects the effectiveness and efficiency of web application penetration testing.
          </p>

          <h5 className="mt-4 mb-3">Study Overview:</h5>
          <ListGroup variant="flush" className="mb-4">
            <ListGroup.Item><strong>1.</strong> Read the study introduction</ListGroup.Item>
            <ListGroup.Item><strong>2.</strong> Review and provide informed consent</ListGroup.Item>
            <ListGroup.Item><strong>3.</strong> Complete a short pre-experiment questionnaire (~5 minutes)</ListGroup.Item>
            <ListGroup.Item><strong>4.</strong> Review the concept of documentation used in this study</ListGroup.Item>
            <ListGroup.Item><strong>5.</strong> Learn how to use the platform to conduct the study</ListGroup.Item>
            <ListGroup.Item><strong>6.</strong> Review experimental design documents (~15 minutes)</ListGroup.Item>
            <ListGroup.Item><strong>7.</strong> Perform penetration testing on a web application and document your findings (~90 minutes)</ListGroup.Item>
            <ListGroup.Item><strong>8.</strong> Complete a short post-experiment questionnaire (~15 minutes)</ListGroup.Item>
            <ListGroup.Item><strong>9.</strong> Debriefing and thank you</ListGroup.Item>
          </ListGroup>

          <p className="mb-2"><strong>Study Period:</strong> December XX - December XX, 2025</p>
          
          <div className="mb-3">
            <strong>Research Team:</strong>
            <ul className="mt-2 mb-0">
              <li>Anh-Duy Tran, KU Leuven, Belgium<br/>
                <small>Email: <a href="mailto:anh-duy.tran@kuleuven.be">anh-duy.tran@kuleuven.be</a> (primary contact)</small>
              </li>
              <li>Koen Yskout, KU Leuven, Belgium</li>
              <li>Wouter Joosen, KU Leuven, Belgium</li>
            </ul>
          </div>

          <div className="experiment-note text-muted small border-top pt-3 mt-4">
            <strong>Note:</strong> This experiment is executed and managed by <em>DistriNet @ KU Leuven</em>, following all ethical and compliance standards. All data is used strictly for research purposes. Please do not attempt to cheat, manipulate, or attack the testing platform. Your cooperation ensures the integrity and success of this research.
          </div>

          <div className="d-grid mt-4">
            <Button
              variant="primary"
              size="lg"
              onClick={handleConsent}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" /> Please wait...
                </>
              ) : (
                '🚀 Read Informed Consent '
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Dashboard;
